import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_STYLE = {
  "ON JOB": "border-[#FF3B30]/40 bg-[#FF3B30]/15 text-[#FF5247]",
  AVAILABLE: "border-[#10B981]/40 bg-[#10B981]/10 text-[#10B981]",
  OFFLINE: "border-zinc-600 bg-zinc-500/10 text-zinc-500",
};
const NEXT_STATUS = { AVAILABLE: "ON JOB", "ON JOB": "OFFLINE", OFFLINE: "AVAILABLE" };

const stationNum = (code) => {
  const n = parseInt(String(code).slice(2), 10);
  return isNaN(n) ? 999 : n;
};

// db stations use "FS1" while the pager feed uses zero-padded "FS01"
const normStation = (code) => {
  const m = String(code).match(/^([A-Z]+)0*(\d+)$/i);
  return m ? m[1].toUpperCase() + m[2] : String(code).toUpperCase();
};

export default function ApplianceBoard() {
  const [apps, setApps] = useState(null);
  const [stationNames, setStationNames] = useState({});

  const load = () =>
    Promise.all([api.get("/appliances/status"), api.get("/stations/appliances")])
      .then(([a, s]) => {
        setApps(a.data.appliances);
        const names = {};
        s.data.stations.forEach((st) => {
          names[normStation(st.code)] = st.name;
        });
        setStationNames(names);
      })
      .catch(() => setApps([]));

  useEffect(() => {
    load();
  }, []);

  const cycle = async (a) => {
    try {
      if (a.source === "manual") {
        await api.put(`/appliances/${a.callsign}/status`, {
          status: NEXT_STATUS[a.status] || "AVAILABLE",
        });
      } else {
        await api.put(`/stations/${a.station}/appliances`, {
          callsign: a.callsign,
          name: a.name,
          status: NEXT_STATUS[a.status] || "AVAILABLE",
        });
      }
      load();
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/stations/${a.station}/appliances/${a.callsign}`);
      toast.success(`${a.callsign} removed`);
      load();
    } catch (e) {
      toast.error("Could not remove appliance");
    }
  };

  if (apps === null) {
    return (
      <p
        data-testid="appliance-board-loading"
        className="py-10 text-center font-jb text-[11px] tracking-[0.3em] text-zinc-600"
      >
        READING PAGER TRAFFIC…
      </p>
    );
  }

  const byStation = {};
  apps.forEach((a) => {
    (byStation[a.station] = byStation[a.station] || []).push(a);
  });
  Object.values(byStation).forEach((list) =>
    list.sort((x, y) => x.callsign.localeCompare(y.callsign))
  );
  const codes = Object.keys(byStation).sort(
    (x, y) => stationNum(x) - stationNum(y) || x.localeCompare(y)
  );
  const onJob = apps.filter((a) => a.status === "ON JOB").length;

  return (
    <div data-testid="appliance-board">
      <p className="mb-4 font-jb text-[9px] leading-relaxed tracking-[0.25em] text-zinc-600">
        {codes.length} STATIONS · {apps.length} APPLIANCES TRACKED · {onJob} ON
        A JOB RIGHT NOW · CLICK A STATUS TO CHANGE IT
      </p>
      {apps.length === 0 && (
        <div className="border border-[#27272A] bg-[#0D0D0D]">
          <p className="p-6 text-center font-jb text-[10px] tracking-[0.3em] text-zinc-600">
            NO APPLIANCES SEEN YET — ADD SOME VIA THE FRV STATIONS TAB.
          </p>
        </div>
      )}
      <div className="flex flex-wrap items-start gap-3">
        {codes.map((code) => {
          const list = byStation[code];
          const onJobHere = list.filter((a) => a.status === "ON JOB").length;
          return (
            <div
              key={code}
              data-testid={`station-col-${code}`}
              className="w-full border border-[#27272A] bg-[#0D0D0D] sm:w-[calc(50%-6px)] lg:w-[calc(33.333%-8px)] xl:w-[calc(25%-9px)]"
            >
              <div className="border-b border-[#1C1C1C] px-3 py-2.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="bg-[#FF6B00]/15 px-1.5 py-0.5 font-jb text-[10px] font-bold text-[#FF6B00]">
                    {code}
                  </span>
                  <span className="font-jb text-[9px] tracking-[0.2em] text-zinc-600">
                    {list.length} UNITS
                    {onJobHere > 0 ? (
                      <span className="text-[#FF5247]"> · {onJobHere} ON JOB</span>
                    ) : (
                      ""
                    )}
                  </span>
                </div>
                <p className="mt-1.5 truncate font-chivo text-sm font-bold tracking-tight text-white">
                  {(stationNames[normStation(code)] || code).toUpperCase()}
                </p>
              </div>
              <div>
                {list.map((a) => (
                  <div
                    key={a.callsign}
                    data-testid={`appliance-row-${a.callsign}`}
                    className="flex items-center justify-between gap-2 border-b border-[#1C1C1C] px-3 py-1.5 last:border-b-0"
                  >
                    <div className="flex min-w-0 items-baseline gap-2">
                      <span className="shrink-0 font-jb text-[11px] font-bold text-white">
                        {a.callsign}
                      </span>
                      <span className="truncate font-jb text-[9px] tracking-[0.1em] text-zinc-500">
                        {(a.name || "APPLIANCE").toUpperCase()}
                        {a.source === "pager" ? " · PAGER" : ""}
                      </span>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        data-testid={`status-${a.callsign}`}
                        onClick={() => cycle(a)}
                        title={
                          a.incident
                            ? `${a.incident.code} — ${a.incident.type} · click to change status`
                            : "Click to change status"
                        }
                        className={`border px-1.5 py-0.5 font-jb text-[8px] tracking-[0.15em] transition-opacity hover:opacity-70 ${STATUS_STYLE[a.status] || STATUS_STYLE.AVAILABLE}`}
                      >
                        {a.status === "ON JOB" && a.incident
                          ? `ON JOB · ${a.incident.code}`
                          : a.status}
                      </button>
                      {a.source === "manual" && (
                        <button
                          data-testid={`remove-appliance-${a.callsign}`}
                          onClick={() => remove(a)}
                          aria-label={`Remove ${a.callsign}`}
                          className="border border-[#27272A] px-1 py-0.5 text-zinc-600 transition-colors hover:border-[#FF3B30] hover:text-[#FF5247]"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
